import React, { useContext, useMemo, useState } from "react";
import { DASHBOARD_BALANCE_LOADING_ID } from "testIds";
import moment from "moment";
import styled, { useTheme } from "styled-components";
import { LanguageNameSpaces, TimeRange } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useDashboardStatistics } from "@shared/features/dashboard/hooks";
import { SocketPnlContext } from "@shared/features/dashboard/SocketPnl.context";
import { useTotalPnl } from "@shared/features/socket/hooks";
import { BASE_CHART_TIME_RANGES } from "@shared/common/constants";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { TIME_RANGE_1_DAY } from "common/constants";
import useDashboardHistory from "features/dashboard/common/useDashboardHistory.ts";
import {
  formatCompactMoney,
  formatSignedPercent,
  shownAt,
  splitFraction,
} from "features/portfolio/NewLook/format.ts";
import BalanceChart, {
  BalancePoint,
} from "features/dashboard/instrument/BalanceChart.tsx";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import ChartEmpty from "common/NewLook/ChartEmpty.tsx";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import useValueFlash from "common/NewLook/useValueFlash.ts";
import { formatDateLabel } from "@shared/utils/date";

interface OverviewPanelProps {
  selectedTimeRange: TimeRange;
  setSelectedTimeRange: (timeRange: TimeRange) => void;
  balanceLoading: boolean;
}

const CHART_HEIGHT = 184;

/**
 * The dashboard's hero: what the portfolio is worth, how it moved over the
 * selected range (as a chart, not a tile), and beside it what is at work in
 * open trades.
 */
const OverviewPanel = ({
  selectedTimeRange,
  setSelectedTimeRange,
  balanceLoading,
}: OverviewPanelProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  // Portfolio value: the live total of the accounts in the dashboard's
  // filter, like the rest of the platform; the history for the selected
  // range sums the same accounts and ends on that total, so the headline,
  // its change and the hovered value agree.
  const {
    total,
    totalLoading,
    points: chartPoints,
    precision,
    isUsd,
  } = useDashboardHistory(selectedTimeRange);
  const symbol = isUsd ? "$" : "₿";
  const points = useMemo(
    () =>
      chartPoints.map((point) => ({
        time: Number(point.rawX),
        value: point.y,
      })),
    [chartPoints]
  );
  // Hovering the chart moves the headline to that point in time.
  const [hovered, setHovered] = useState<BalancePoint>();
  const first = points[0]?.value;
  const last = hovered?.value ?? points[points.length - 1]?.value;
  const change = first !== undefined && last !== undefined ? last - first : 0;
  const changePercent = first ? (change / first) * 100 : 0;
  const headline = hovered ? hovered.value : total;

  // Open trades: exposure, unrealized PnL (live) and potential profit.
  const statistics = useDashboardStatistics(false, true);
  const { socketPnlValue } = useContext(SocketPnlContext)!;
  const pnl = useTotalPnl(statistics.openTradesData, socketPnlValue);
  const inTrades = statistics.tradesAmount;
  const exposure = total ? Math.min(1, inTrades / total) : 0;
  const pnlPercent = inTrades ? (pnl / inTrades) * 100 : 0;
  const pnlFlash = useValueFlash(pnl);

  const money = (value: number, digits = precision) =>
    `${
      shownAt(value, digits) < 0 ? "−" : ""
    }${symbol}${getLocaleStringWithPrecision(Math.abs(value), digits)}`;
  const signedMoney = (value: number) =>
    `${shownAt(value, precision) > 0 ? "+" : ""}${money(value)}`;
  const signedPercent = formatSignedPercent;

  const [whole, fraction] = splitFraction(
    getLocaleStringWithPrecision(headline, precision)
  );
  const changeLoading = totalLoading || (balanceLoading && !points.length);

  return (
    <Panel data-testid="dashboard-overview" flush={true} hero={true}>
      <Layout>
        <Main>
          <MainHeader>
            <div>
              <Label>{t("overview.portfolio_value")}</Label>
              {totalLoading ? (
                <span
                  aria-busy="true"
                  data-testid={DASHBOARD_BALANCE_LOADING_ID}
                >
                  <Skeleton width={220} height={40} style={{ marginTop: 6 }} />
                </span>
              ) : (
                <Display>
                  <Currency>{symbol}</Currency>
                  {whole}
                  {fraction && <Fraction>{fraction}</Fraction>}
                </Display>
              )}
              {changeLoading ? (
                <Skeleton
                  width={150}
                  height={14}
                  style={{ margin: "7px 0 1px" }}
                />
              ) : (
                <ChangeLine>
                  <ChangeValue
                    style={{ color: directionColor(tokens, change) }}
                  >
                    {signedMoney(change)}
                  </ChangeValue>
                  <ChangeValue
                    style={{ color: directionColor(tokens, change) }}
                  >
                    {signedPercent(changePercent)}
                  </ChangeValue>
                  <Meta>
                    {hovered && points.length
                      ? t("overview.since", {
                          time: formatDateLabel(
                            moment.unix(points[0].time),
                            selectedTimeRange === TIME_RANGE_1_DAY
                              ? "HH:mm"
                              : "MMM D"
                          ),
                        })
                      : t("overview.over_range", {
                          range: selectedTimeRange.toUpperCase(),
                        })}
                  </Meta>
                </ChangeLine>
              )}
            </div>
            <SegmentTabs
              options={BASE_CHART_TIME_RANGES}
              value={selectedTimeRange}
              onChange={setSelectedTimeRange}
              ariaLabel={t("overview.range_label")}
            />
          </MainHeader>
          <ChartArea>
            {balanceLoading && !points.length ? (
              <Skeleton width="100%" height={CHART_HEIGHT} radius={8} />
            ) : points.length > 1 ? (
              <BalanceChart
                points={points}
                height={CHART_HEIGHT}
                intraday={selectedTimeRange === TIME_RANGE_1_DAY}
                hourly={true}
                onActiveChange={setHovered}
                formatValue={money}
                formatAxisValue={(value, step) =>
                  formatCompactMoney(value, symbol, step)
                }
                ariaLabel={t("overview.chart_label", {
                  range: selectedTimeRange.toUpperCase(),
                })}
              />
            ) : (
              <ChartEmpty height={CHART_HEIGHT}>
                {t("overview.no_history")}
              </ChartEmpty>
            )}
          </ChartArea>
        </Main>

        <Side>
          <Metric>
            <Label>{t("statistics.open_trades_amount")}</Label>
            <MetricValue>{money(inTrades)}</MetricValue>
            <ExposureTrack aria-hidden={true}>
              {!totalLoading && (
                <ExposureFill style={{ width: `${exposure * 100}%` }} />
              )}
            </ExposureTrack>
            {totalLoading ? (
              <Skeleton width={90} height={12} style={{ margin: "2px 0" }} />
            ) : (
              <Meta>
                {t("overview.share_of_portfolio", {
                  percent: `${Math.round(exposure * 100)}%`,
                })}
              </Meta>
            )}
          </Metric>
          <Metric>
            <Label>{t("statistics.open_trades_pnl")}</Label>
            <MetricValue
              className={pnlFlash}
              style={{ color: directionColor(tokens, pnl) }}
            >
              {signedMoney(pnl)}
            </MetricValue>
            <Meta>
              {t("overview.return_on_trades", {
                percent: signedPercent(pnlPercent),
              })}
            </Meta>
          </Metric>
          <Metric>
            <Label>{t("statistics.potential_profit")}</Label>
            <MetricValue>{money(statistics.tradesPotentialProfit)}</MetricValue>
            <Meta>{t("overview.potential_profit_hint")}</Meta>
          </Metric>
        </Side>
      </Layout>
    </Panel>
  );
};

export default React.memo(OverviewPanel);

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Main = styled.div`
  min-width: 0;
  padding: 20px 20px 16px 24px;
`;
const MainHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;
const Label = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Display = styled.div`
  margin-top: 4px;
  ${newLookType.display};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Currency = styled.span`
  margin-right: 1px;
  font-size: 0.72em;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Fraction = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const ChangeLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 6px;
`;
const ChangeValue = styled.span`
  ${newLookType.meta};
  font-size: 13px;
  font-weight: 500;
`;
const Meta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const ChartArea = styled.div`
  margin-top: 20px;
`;
const Side = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 24px 20px;
  border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: ${(p) => getNewLookTokens(p.theme).well};
  border-radius: 0 12px 12px 0;
  @media (max-width: 1100px) {
    /* Stacked under the chart, the three figures read across in one row. */
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: 24px;
    border-left: none;
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    border-radius: 0 0 12px 12px;
  }
`;
const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  &:last-child {
    padding-bottom: 0;
  }
  @media (max-width: 1100px) {
    padding: 8px 0 0;
    & + & {
      border-top: none;
    }
  }
`;
const MetricValue = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  border-radius: 4px;
  width: fit-content;
`;
const ExposureTrack = styled.div`
  height: 4px;
  margin: 4px 0 2px;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  overflow: hidden;
`;
const ExposureFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).accent};
  transition: width 300ms ease;
`;
